import { useEffect, useMemo, useState } from "react";
import {
  Boxes, ClipboardCheck, LayoutDashboard, LogOut, Menu, PackageSearch,
  ShieldCheck, Users, Warehouse as WarehouseIcon, X, LineChart, Package, ArrowRightLeft, PlugZap, Tags
} from "lucide-react";
import { api, decodeSession, setAccessToken } from "./api";
import type { Session } from "./types";
import { LoginPage } from "./components/LoginPage";
import { DashboardPage } from "./pages/DashboardPage";
import { OrdersPage } from "./pages/OrdersPage";
import { PickingPage } from "./pages/PickingPage";
import { InventoryPage } from "./pages/InventoryPage";
import { WarehousesPage } from "./pages/WarehousesPage";
import { EmployeesPage } from "./pages/EmployeesPage";
import { AnalyticsPage } from "./pages/AnalyticsPage";
import { CatalogPage } from "./pages/CatalogPage";
import { OneCInboundPage } from "./pages/OneCInboundPage";
import { PlacementPage } from "./pages/PlacementPage";
import { IntegrationPage } from "./pages/IntegrationPage";
import { CategoriesPage } from "./pages/CategoriesPage";
import { navigateTo, readPage, readRouteParams, resolvePage, subscribeNavigation } from "./navigation";
import type { Page } from "./navigation";
const SESSION_KEY = "wms.session";

function clearStoredSession() {
  localStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(SESSION_KEY);
}

function loadSession(): Session | null {
  try {
    const persistentValue = localStorage.getItem(SESSION_KEY);
    const value = persistentValue ?? sessionStorage.getItem(SESSION_KEY);
    if (!value) return null;
    const stored = JSON.parse(value) as Session;
    const session = decodeSession(stored.token, stored.expiresAt);
    if (new Date(session.expiresAt).getTime() <= Date.now()) {
      clearStoredSession();
      return null;
    }
    if (!persistentValue) localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    sessionStorage.removeItem(SESSION_KEY);
    setAccessToken(session.token);
    return session;
  } catch {
    clearStoredSession();
    return null;
  }
}

export default function App() {
  const [session, setSession] = useState<Session | null>(loadSession);
  const [route, setRoute] = useState(() => window.location.hash);
  const page = resolvePage(route, session?.roles);
  const targetWarehouseId = readRouteParams(route).get("warehouse") ?? undefined;
  const [menuOpen, setMenuOpen] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const canManage = session?.roles.some(role => role === "admin" || role === "warehouse_supervisor") ?? false;
  const canWarehouse = session?.roles.some(role => ["admin","warehouse_supervisor","picker"].includes(role)) ?? false;

  useEffect(() => subscribeNavigation(() => setRoute(window.location.hash)), []);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "auto" }); }, [page, session?.email]);

  useEffect(() => {
    if (session && readPage(route) !== page) navigateTo(page, {}, true);
  }, [session, page, route]);

  useEffect(() => {
    const unauthorized = () => { clearStoredSession(); setSession(null); };
    window.addEventListener("wms:unauthorized", unauthorized);
    return () => window.removeEventListener("wms:unauthorized", unauthorized);
  }, []);

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => { window.removeEventListener("online", update); window.removeEventListener("offline", update); };
  }, []);

  useEffect(() => {
    if (!session) return;
    let disposed = false, inFlight = false;
    const checkExpiry = async () => {
      const left = new Date(session.expiresAt).getTime() - Date.now();
      if (left <= 0) { logout(); return; }
      if (left > 5 * 60_000 || inFlight || session.email.endsWith("@native.invalid")) return;
      inFlight = true;
      try {
        const result = await api.refreshSession();
        if (!disposed) {
          const next = decodeSession(result.accessToken, result.expiresAt);
          setAccessToken(next.token); localStorage.setItem(SESSION_KEY, JSON.stringify(next)); setSession(next);
        }
      } catch { /* 401 clears the session centrally; transient failures wait for the next tick. */ }
      finally { inFlight = false; }
    };
    void checkExpiry();
    const timer = window.setInterval(() => void checkExpiry(), 30_000);
    return () => { disposed = true; window.clearInterval(timer); };
  }, [session]);

  const nav = useMemo(() => {
    const items: Array<{ id: Page; label: string; icon: typeof LayoutDashboard }> = [];
    if (canManage) items.push(
      { id: "dashboard", label: "Обзор", icon: LayoutDashboard },
      { id: "orders", label: "Заказы", icon: ClipboardCheck }
    );
    if (canWarehouse) items.push(
      { id: "inbound", label: "Приёмка и возвраты", icon: Package },
      { id: "placement", label: "Размещение", icon: ArrowRightLeft },
      { id: "picking", label: "Сборка", icon: PackageSearch }
    );
    if (canManage) items.push(
      { id: "catalog", label: "Товары", icon: Package },
      { id: "categories", label: "Категории", icon: Tags },
      { id: "inventory", label: "Остатки", icon: Boxes },
      { id: "warehouses", label: "Ячейки", icon: WarehouseIcon },
      { id: "employees", label: "Сотрудники", icon: Users },
      { id: "analytics", label: "Аналитика", icon: LineChart },
      { id: "integration", label: "Интеграция 1С", icon: PlugZap }
    );
    return items;
  }, [canManage, canWarehouse]);

  function login(email: string, password: string) {
    return api.login(email, password).then(result => {
      const next = decodeSession(result.accessToken, result.expiresAt);
      setAccessToken(next.token);
      localStorage.setItem(SESSION_KEY, JSON.stringify(next));
      sessionStorage.removeItem(SESSION_KEY);
      setSession(next);
    });
  }

  function logout() {
    setAccessToken("");
    clearStoredSession();
    setSession(null);
  }

  if (!session) return <LoginPage onLogin={login} />;

  const content = (() => {
    switch (page) {
      case "dashboard": return <DashboardPage onOpenOrders={() => navigateTo("orders")} onOpenWarehouses={(id) => navigateTo("warehouses", { warehouse: id })} />;
      case "orders": return <OrdersPage actor={session.email} canControlLocalFinance={session.roles.includes("admin")} canReview={canManage} />;
      case "inbound": return <OneCInboundPage canManage={canManage} actor={session.email} />;
      case "placement": return <PlacementPage canManage={canManage} actor={session.email} />;
      case "picking": return <PickingPage actor={session.email} />;
      case "inventory": return <InventoryPage />;
      case "warehouses": return <WarehousesPage initialWarehouseId={targetWarehouseId} />;
      case "catalog": return <CatalogPage />;
      case "categories": return <CategoriesPage />;
      case "employees": return <EmployeesPage currentRoles={session.roles} />;
      case "analytics": return <AnalyticsPage />;
      case "integration": return <IntegrationPage />;
    }
  })();

  return (
    <div className="app-shell">
      <aside className={`sidebar ${menuOpen ? "sidebar--open" : ""}`}>
        <div className="brand">
          <div className="nord-brand-lockup"><img className="nord-logo" src="/brand/nord.svg" alt="Nord — оконные системы"/><span className="nord-service-label">Складская система WMS</span></div>
          <button className="icon-button sidebar__close" onClick={() => setMenuOpen(false)} aria-label="Закрыть меню"><X /></button>
        </div>
        <nav className="nav-list" aria-label="Основное меню">
          {nav.map(item => <button key={item.id} className={page === item.id ? "active" : ""} onClick={() => { navigateTo(item.id); setMenuOpen(false); }}>
            <item.icon size={20} /><span>{item.label}</span>
          </button>)}
        </nav>
        <div className="sidebar__footer">
          <div className="user-card"><span className="avatar">{session.name.slice(0, 1).toUpperCase()}</span><span><b>{session.name}</b><small>{session.email}</small></span></div>
          <button className="nav-logout" onClick={logout}><LogOut size={18} />Выйти</button>
        </div>
      </aside>
      {menuOpen && <button className="backdrop" onClick={() => setMenuOpen(false)} aria-label="Закрыть меню" />}
      <main className="main-area">
        <header className="topbar">
          <button className="icon-button menu-button" onClick={() => setMenuOpen(true)} aria-label="Открыть меню"><Menu /></button>
          <div className="topbar__title">{nav.find(x => x.id === page)?.label}</div>
          <div className={`connection ${online ? "online" : "offline"}`}><span />{online ? "В сети" : "Нет связи"}</div>
          <div className="secure"><ShieldCheck size={17} />Защищено</div>
        </header>
        <div className="page-wrap">{content}</div>
      </main>
      <nav className="bottom-nav" aria-label="Мобильное меню">
        {nav.slice(0, 5).map(item => <button key={item.id} className={page === item.id ? "active" : ""} onClick={() => navigateTo(item.id)}>
          <item.icon size={21} /><span>{item.label}</span>
        </button>)}
      </nav>
    </div>
  );
}

